import React, { useEffect, useState } from 'react';
import { Plus, Minus, ChevronRight, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../../lib/api';

export default function NewsSection() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    async function loadNews() {
      const data = await fetchCollection('news_items');
      // Sort by date descending robustly
      const sorted = [...data].sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
      });
      setNews(sorted);
      setLoading(false);
    }
    loadNews();
  }, []);

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('da-DK', options);
  };

  const displayedNews = showAll ? news : news.slice(0, 2);

  if (loading) return (
    <div className="py-24 text-center">
      <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent mx-auto" />
    </div>
  );

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div className="max-w-xl">
            <div className="flex items-center gap-4 mb-4">
              <Bell size={20} className="text-black" />
              <span className="text-black text-xs tracking-[0.4em] uppercase font-bold">Aktuelt</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-sans font-medium text-black leading-tight tracking-tight uppercase">SE NYHEDER HERUNDER</h2>
          </div>
          {news.length > 2 && (
            <button 
              onClick={() => setShowAll(!showAll)}
              className="text-xs tracking-[0.3em] uppercase font-bold text-slate-500 hover:text-black transition-colors flex items-center gap-2 border-b border-slate-200 pb-2"
            >
              {showAll ? 'Vis færre' : 'Se alle nyheder'} <ChevronRight size={14} className={`transition-transform ${showAll ? 'rotate-90' : ''}`} />
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-0 border-t border-slate-100">
          {displayedNews.map((item) => {
            const isExpanded = expandedItems[item.id];
            return (
              <div 
                key={item.id}
                className="group border-b border-slate-100 py-8 flex flex-col md:flex-row gap-8 items-start hover:bg-slate-50/50 transition-colors px-4 -mx-4 cursor-pointer"
                onClick={() => toggleExpand(item.id)}
              >
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2 font-bold">
                    {formatDate(item.date)}
                  </div>
                  <h3 className="text-xl font-sans font-medium text-black group-hover:text-black transition-colors mb-2 leading-tight tracking-tight">
                    {item.title.da}
                  </h3>
                  <div 
                    className={`text-slate-600 text-sm leading-relaxed opacity-80 transition-all duration-300 ${isExpanded ? '' : 'line-clamp-1'}`}
                    dangerouslySetInnerHTML={{ __html: item.content.da }}
                  />
                  <div className="mt-4 flex items-center gap-1 text-slate-500 group-hover:text-black transition-colors font-bold text-[10px] uppercase tracking-[0.2em]">
                    <span>{isExpanded ? 'Vis mindre' : 'Læs mere'}</span>
                    {isExpanded ? <Minus size={10} /> : <Plus size={10} />}
                  </div>
                </div>
                <div className={`w-10 h-10 border border-slate-200 flex items-center justify-center transition-all ${isExpanded ? 'bg-black border-black' : 'group-hover:bg-black group-hover:border-black'}`}>
                  {isExpanded ? (
                    <Minus size={20} className="text-white transition-colors" />
                  ) : (
                    <Plus size={20} className="text-slate-400 group-hover:text-white transition-colors" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

